const ALLOWED_ENVIRONMENTS = new Set([
  'development',
  'staging',
  'production',
  'test',
]);

export class ConfigValidationError extends Error {
  constructor(message, details = []) {
    super(message);
    this.name = 'ConfigValidationError';
    this.details = details;
  }
}

const normalizeBaseUrl = (urlString) => {
  const url = new URL(urlString);
  url.pathname = url.pathname.replace(/\/+$/, '') || '/';
  return url.toString().replace(/\/+$/, '');
};

const readBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value).toLowerCase() === 'true';
};

const validateAbsoluteUrl = ({ value, name, isProduction, details }) => {
  if (!value) {
    details.push(`Missing required ${name}.`);
    return '';
  }
  try {
    const parsed = new URL(value);
    if (isProduction && parsed.protocol !== 'https:') {
      details.push(`${name} must use HTTPS in production.`);
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    details.push(`${name} must be a valid absolute URL.`);
    return '';
  }
};

const resolveEnvironment = ({ runtimeConfig, env }) => {
  const runtimeEnvironment =
    runtimeConfig.ENVIRONMENT || runtimeConfig.environment || undefined;
  const buildEnvironment =
    env.REACT_APP_ENVIRONMENT || env.REACT_APP_APP_ENVIRONMENT || undefined;

  if (runtimeEnvironment) return runtimeEnvironment;
  if (buildEnvironment) return buildEnvironment;
  if (env.NODE_ENV && ALLOWED_ENVIRONMENTS.has(env.NODE_ENV)) {
    return env.NODE_ENV;
  }
  return '';
};

export const validateRuntimeConfig = ({
  runtimeConfig = {},
  env = {},
} = {}) => {
  const details = [];
  const isDevelopment = env.NODE_ENV !== 'production';
  const isProduction = env.NODE_ENV === 'production';

  const apiBaseUrlInput =
    runtimeConfig.API_BASE_URL ||
    runtimeConfig.REACT_APP_API_BASE_URL ||
    env.REACT_APP_API_BASE_URL ||
    (isDevelopment ? 'http://127.0.0.1:8000' : '');

  if (!apiBaseUrlInput) {
    details.push(
      'Missing required API base URL. Set API_BASE_URL in runtime config or REACT_APP_API_BASE_URL in build env.'
    );
  }

  let apiBaseUrl = '';
  if (apiBaseUrlInput) {
    try {
      const parsed = new URL(apiBaseUrlInput);

      if (isProduction && parsed.protocol !== 'https:') {
        details.push('API_BASE_URL must use HTTPS in production.');
      }

      apiBaseUrl = normalizeBaseUrl(apiBaseUrlInput);
    } catch {
      details.push('API_BASE_URL must be a valid absolute URL.');
    }
  }

  const environment = resolveEnvironment({ runtimeConfig, env });
  if (!environment) {
    details.push(
      'Missing required environment. Set ENVIRONMENT in runtime config or REACT_APP_ENVIRONMENT in build env.'
    );
  } else if (!ALLOWED_ENVIRONMENTS.has(environment)) {
    details.push(
      `ENVIRONMENT must be one of: ${Array.from(ALLOWED_ENVIRONMENTS).join(', ')}.`
    );
  }

  const authEnabled = readBoolean(
    runtimeConfig.AUTH_ENABLED ?? env.REACT_APP_AUTH_ENABLED,
    false
  );
  let auth = { enabled: false };
  if (authEnabled) {
    const authority = validateAbsoluteUrl({
      value: runtimeConfig.AUTH_AUTHORITY || env.REACT_APP_AUTH_AUTHORITY,
      name: 'AUTH_AUTHORITY',
      isProduction,
      details,
    });
    const domain = validateAbsoluteUrl({
      value: runtimeConfig.AUTH_DOMAIN || env.REACT_APP_AUTH_DOMAIN,
      name: 'AUTH_DOMAIN',
      isProduction,
      details,
    });
    const redirectUri = validateAbsoluteUrl({
      value: runtimeConfig.AUTH_REDIRECT_URI || env.REACT_APP_AUTH_REDIRECT_URI,
      name: 'AUTH_REDIRECT_URI',
      isProduction,
      details,
    });
    const logoutUri = validateAbsoluteUrl({
      value: runtimeConfig.AUTH_LOGOUT_URI || env.REACT_APP_AUTH_LOGOUT_URI,
      name: 'AUTH_LOGOUT_URI',
      isProduction,
      details,
    });
    const clientId =
      runtimeConfig.AUTH_CLIENT_ID || env.REACT_APP_AUTH_CLIENT_ID || '';
    if (!clientId) details.push('Missing required AUTH_CLIENT_ID.');

    auth = {
      enabled: true,
      authority,
      domain,
      clientId,
      redirectUri,
      logoutUri,
      scope: runtimeConfig.AUTH_SCOPE || 'openid profile',
    };
  }

  if (details.length > 0) {
    throw new ConfigValidationError(
      'Runtime configuration validation failed.',
      details
    );
  }

  return {
    apiBaseUrl,
    environment,
    release: runtimeConfig.RELEASE || runtimeConfig.release || null,
    isDevelopment,
    isProduction,
    mode: env.NODE_ENV || environment,
    auth,
  };
};
